import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// ===== FETCH ALL PRICING PLANS =====
export const fetchPricingPlans = createAsyncThunk(
    'pricing/fetchPlans',
    async (_, { rejectWithValue }) => {
        try {
            console.log('[Pricing] 🔍 Fetching pricing plans...');
            const response = await apiClient.get(API_ENDPOINTS.PRICING);
            console.log('[Pricing] ✅ Plans response:', response.data);
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message, data: data || [] };
            }
            return rejectWithValue({ message: message || 'Failed to fetch pricing plans' });
        } catch (error) {
            console.error('[Pricing] ❌ Fetch plans error:', error);
            return rejectWithValue({ message: error.message || 'Network error' });
        }
    }
);

// ===== FETCH CURRENT SUBSCRIPTION SUMMARY =====
export const fetchPricingSummary = createAsyncThunk(
    'pricing/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            console.log('[Pricing] 🔍 Fetching pricing summary...');
            const response = await apiClient.get(API_ENDPOINTS.PRICING_SUMMARY);
            console.log('[Pricing] ✅ Summary response:', response.data);
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message, data };
            }
            return rejectWithValue({ message: message || 'Failed to fetch subscription summary' });
        } catch (error) {
            console.error('[Pricing] ❌ Fetch summary error:', error);
            return rejectWithValue({ message: error.message || 'Network error' });
        }
    }
);

// ===== CHANGE ORGANIZATION PLAN =====
export const changePlan = createAsyncThunk(
    'pricing/changePlan',
    async (pricingId, { rejectWithValue }) => {
        try {
            console.log('[Pricing] 🔁 Changing plan with pricing_id:', pricingId);
            const response = await apiClient.post(API_ENDPOINTS.CHANGE_PLAN, { pricing_id: pricingId });
            console.log('[Pricing] ✅ Change plan response:', response.data);
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message, data };
            }
            return rejectWithValue({ message: message || 'Failed to change plan' });
        } catch (error) {
            // Log the full error details to help debug 403 and other issues
            console.error('[Pricing] ❌ Change plan error:');
            if (error.response) {
                // The request was made and the server responded with a status code outside 2xx
                console.error('[Pricing] Status:', error.response.status);
                console.error('[Pricing] Headers:', error.response.headers);
                console.error('[Pricing] Data:', error.response.data);
                // Use the server's error message if available, else fallback
                const serverMessage = error.response.data?.message || error.message;
                return rejectWithValue({ message: serverMessage, status: error.response.status });
            } else if (error.request) {
                // The request was made but no response was received
                console.error('[Pricing] No response received:', error.request);
                return rejectWithValue({ message: 'No response from server' });
            } else {
                // Something happened in setting up the request
                console.error('[Pricing] Request setup error:', error.message);
                return rejectWithValue({ message: error.message });
            }
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    plans: [],               // All available pricing plans
    selectedPlan: null,      // The plan the organization is currently subscribed to
    loading: false,
    error: null,
    message: null,
};

const pricingSlice = createSlice({
    name: 'pricing',
    initialState,
    reducers: {
        clearPricingError: (state) => {
            state.error = null;
        },
        clearPricingMessage: (state) => {
            state.message = null;
        },
        clearPricingState: (state) => {
            state.plans = [];
            state.selectedPlan = null;
            state.loading = false;
            state.error = null;
            state.message = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== FETCH PRICING PLANS =====
            .addCase(fetchPricingPlans.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(fetchPricingPlans.fulfilled, (state, action) => {
                state.loading = false;
                state.plans = action.payload.data;
                state.message = action.payload.message || 'Pricing plans loaded';
                state.error = null;
            })
            .addCase(fetchPricingPlans.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to load pricing plans';
                state.message = null;
                state.plans = [];
            })

            // ===== FETCH PRICING SUMMARY =====
            .addCase(fetchPricingSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(fetchPricingSummary.fulfilled, (state, action) => {
                state.loading = false;
                // Assume the API returns subscription data with a 'pricing' object
                const sub = action.payload.data?.subscription;
                if (sub && sub.pricing) {
                    state.selectedPlan = {
                        id: sub.pricing.id,
                        name: sub.pricing.name,
                        price: sub.pricing.is_custom ? 'Custom' : `${sub.pricing.currency}${sub.pricing.price}`,
                        is_custom: sub.pricing.is_custom,
                        currency: sub.pricing.currency,
                        period: sub.pricing.period,
                        features: sub.pricing.features,
                        expires_at: sub.expires_at,
                        status: sub.status,
                    };
                }
                state.message = action.payload.message || 'Subscription loaded';
                state.error = null;
            })
            .addCase(fetchPricingSummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to load subscription';
                state.message = null;
            })

            // ===== CHANGE PLAN =====
            .addCase(changePlan.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(changePlan.fulfilled, (state, action) => {
                state.loading = false;
                const sub = action.payload.data?.subscription;
                if (sub && sub.pricing) {
                    state.selectedPlan = {
                        id: sub.pricing.id,
                        name: sub.pricing.name,
                        price: sub.pricing.is_custom ? 'Custom' : `${sub.pricing.currency}${sub.pricing.price}`,
                        is_custom: sub.pricing.is_custom,
                        currency: sub.pricing.currency,
                        period: sub.pricing.period,
                        features: sub.pricing.features,
                        expires_at: sub.expires_at,
                        status: sub.status,
                    };
                }
                state.message = action.payload.message || 'Plan changed successfully';
                state.error = null;
            })
            .addCase(changePlan.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || 'Failed to change plan';
                state.message = null;
            });
    },
});

// ===== SELECTORS =====
export const selectPricingPlans = (state) => state.pricing?.plans || [];
export const selectSelectedPlan = (state) => state.pricing?.selectedPlan || null;
export const selectPricingLoading = (state) => state.pricing?.loading || false;
export const selectPricingError = (state) => state.pricing?.error || null;
export const selectPricingMessage = (state) => state.pricing?.message || null;

export const {
    clearPricingError,
    clearPricingMessage,
    clearPricingState,
} = pricingSlice.actions;

export default pricingSlice.reducer;